import React from 'react';
import { Dialog, DialogContent, DialogTitle, IconButton, Typography, Box } from '@material-ui/core';
import { Close as CloseIcon, Email as EmailIcon, PhoneIphone as PhoneIcon } from '@material-ui/icons';
import { unpackToDateTime } from '@campfire/hot-date';
import { VolunteerCommonProfileEmail } from '../../__generated__/VolunteerCommonProfileEmail';
import { VolunteerCommonProfileSms } from '../../__generated__/VolunteerCommonProfileSms';
import { SmsLogTypeParsed } from '../../VolunteerCommonProfileDialog';

interface CommunicationDetailDialogProps {
  open: boolean;
  onClose: () => void;
  communication: VolunteerCommonProfileEmail | VolunteerCommonProfileSms | SmsLogTypeParsed | null;
}

export const CommunicationDetailDialog = ({ open, onClose, communication }: CommunicationDetailDialogProps) => {
  const renderEmailContent = (email: VolunteerCommonProfileEmail) => {
    return (
      <Box
        style={{
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          padding: '12px',
          backgroundColor: '#fafafa',
        }}
      >
        <Typography style={{ fontWeight: 600, marginBottom: '4px', fontSize: '14px' }}>Subject:</Typography>
        <Typography style={{ marginBottom: '8px', fontSize: '14px' }}>{email.subject}</Typography>

        <Typography style={{ fontWeight: 600, marginBottom: '4px', fontSize: '14px' }}>From:</Typography>
        <Typography style={{ marginBottom: '8px', fontSize: '14px' }}>{email.sentBy || 'System'}</Typography>

        <Typography style={{ fontWeight: 600, marginBottom: '4px', fontSize: '14px' }}>To:</Typography>
        <Typography style={{ marginBottom: '8px', fontSize: '14px' }}>{email.toEmail}</Typography>

        <Typography style={{ fontWeight: 600, marginBottom: '4px', fontSize: '14px' }}>Sent:</Typography>
        <Typography style={{ marginBottom: '8px', fontSize: '14px' }}>
          {email.sentAt ? unpackToDateTime(email.sentAt).toFormat('h:mm a, dd LLLL yyyy') : 'Unknown'}
        </Typography>

        <Typography style={{ fontWeight: 600, marginBottom: '4px', fontSize: '14px' }}>Body:</Typography>
        <Box
          style={{
            color: '#333',
            backgroundColor: '#fff',
            padding: '8px',
            borderRadius: '4px',
            border: '1px solid #ddd',
            fontSize: '14px',
            lineHeight: '1.4',
          }}
          dangerouslySetInnerHTML={{ __html: email.body || 'No body content available' }}
        />
      </Box>
    );
  };

  const renderSmsContent = (sms: VolunteerCommonProfileSms) => {
    return (
      <Box>
        <Box display='flex' justifyContent='flex-end' mb={1}>
          <Box
            style={{
              backgroundColor: '#1976d2',
              color: 'white',
              padding: '8px 12px',
              borderRadius: '18px 18px 4px 18px',
              maxWidth: '70%',
              wordBreak: 'break-word',
            }}
          >
            <Typography style={{ fontSize: '14px' }}>{sms.body}</Typography>
          </Box>
        </Box>

        <Box style={{ textAlign: 'right' }}>
          <Typography style={{ color: '#666', fontSize: '12px' }}>
            Sent to {sms.contactNumber} • {unpackToDateTime(sms.sentAt).toFormat('h:mm a, dd LLLL yyyy')}
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderSmsLogContent = (smsLog: SmsLogTypeParsed) => {
    // Parse the messageData to extract actual message content
    const getMessageContent = (messageData: string) => {
      try {
        // Check if it's already a plain text message
        if (typeof messageData === 'string' && !messageData.startsWith('O:') && !messageData.startsWith('{')) {
          return messageData;
        }

        // Try to parse as JSON first
        const parsed = JSON.parse(messageData);

        // Check for common content fields in the parsed object
        if (parsed.content) {
          return parsed.content;
        }
        if (parsed.message) {
          return parsed.message;
        }
        if (parsed.body) {
          return parsed.body;
        }
        if (parsed.text) {
          return parsed.text;
        }

        // If no standard content field found, return the original string
        return messageData;
      } catch (error) {
        // For PHP serialized data (starts with O:), try to extract content field
        if (typeof messageData === 'string' && messageData.includes('s:7:"content"')) {
          // Look for content field in PHP serialized format
          const contentMatch = messageData.match(/s:7:"content";s:\d+:"([^"]+)"/);
          if (contentMatch && contentMatch[1]) {
            return contentMatch[1];
          }
        }

        // If parsing fails, return the original string
        return messageData;
      }
    };

    const messageContent = smsLog.messageData ? getMessageContent(smsLog.messageData) : 'No message data available';

    // Parse the messageData for reply detection
    const isReply = smsLog.direction;
    let replyStatus = 'N/A';

    if (isReply && messageContent) {
      const messageText = messageContent.toLowerCase().trim();
      if (messageText === 'y' || messageText === 'yes') {
        replyStatus = 'Y';
      } else if (messageText === 'n' || messageText === 'no') {
        replyStatus = 'N';
      }
    }

    return (
      <Box>
        <Box display='flex' justifyContent={isReply ? 'flex-start' : 'flex-end'} mb={1}>
          <Box
            style={{
              backgroundColor: isReply ? '#e0e0e0' : '#1976d2',
              color: isReply ? '#333' : 'white',
              padding: '8px 12px',
              borderRadius: isReply ? '18px 18px 18px 4px' : '18px 18px 4px 18px',
              maxWidth: '70%',
              wordBreak: 'break-word',
            }}
          >
            <Typography style={{ fontSize: '14px' }}>{messageContent}</Typography>
          </Box>
        </Box>

        {isReply && (
          <Box
            style={{
              backgroundColor: '#f5f5f5',
              padding: '8px',
              borderRadius: '6px',
              marginBottom: '8px',
            }}
          >
            <Typography style={{ fontWeight: 600, marginBottom: '2px', fontSize: '12px' }}>Reply Status:</Typography>
            <Typography
              style={{
                fontSize: '16px',
                fontWeight: 600,
                color: replyStatus === 'Y' ? '#4caf50' : replyStatus === 'N' ? '#f44336' : '#666',
              }}
            >
              {replyStatus}
            </Typography>
          </Box>
        )}

        <Box style={{ textAlign: isReply ? 'left' : 'right' }}>
          <Typography style={{ color: '#666', fontSize: '12px' }}>
            {isReply ? 'Received' : 'Sent'} • {unpackToDateTime(smsLog.sentAt).toFormat('h:mm a, dd LLLL yyyy')}
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderContent = () => {
    if (!communication) return null;

    if (communication.__typename === 'EMAIL_PostmarkOutboundEmailType') {
      return renderEmailContent(communication as VolunteerCommonProfileEmail);
    }

    if (communication.__typename === 'EMAIL_OutboundSmsType') {
      return renderSmsContent(communication as VolunteerCommonProfileSms);
    }

    return renderSmsLogContent(communication as SmsLogTypeParsed);
  };

  const getCommunicationTypeChip = () => {
    if (!communication) return null;

    if (communication.__typename === 'EMAIL_PostmarkOutboundEmailType') {
      return (
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#d9d9d9',
            borderRadius: '3px',
            padding: '4px 8px',
            marginLeft: '8px',
          }}
        >
          <EmailIcon style={{ width: '10px', height: '10px', color: '#545454', marginRight: '4px' }} />
          <Typography style={{ fontWeight: 600, fontSize: '10px', color: '#545454' }}>Email</Typography>
        </Box>
      );
    }

    return (
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: '#d9d9d9',
          borderRadius: '3px',
          padding: '4px 8px',
          marginLeft: '8px',
        }}
      >
        <PhoneIcon style={{ width: '10px', height: '10px', color: '#545454', marginRight: '4px' }} />
        <Typography style={{ fontWeight: 600, fontSize: '10px', color: '#545454' }}>SMS</Typography>
      </Box>
    );
  };

  const getDialogSize = () => {
    if (!communication) return 'sm';

    // Use medium size for emails to accommodate full content, small for SMS
    return communication.__typename === 'EMAIL_PostmarkOutboundEmailType' ? 'md' : 'sm';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={getDialogSize()}
      fullWidth
      PaperProps={{
        style: { borderRadius: '12px' },
      }}
    >
      <DialogTitle style={{ padding: '16px 20px 12px' }}>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Box display='flex' alignItems='center'>
            <Typography style={{ fontSize: '20px', fontWeight: 700 }}>Communication Details</Typography>
            {getCommunicationTypeChip()}
          </Box>
          <IconButton onClick={onClose} size='small'>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent style={{ padding: '8px 20px 20px' }}>{renderContent()}</DialogContent>
    </Dialog>
  );
};
